import React, {FC, useState} from 'react';
import utils from 'utils';
import settings from 'settings/site';
import {IPublicEvent} from 'types/publicEvent';
import {PublicEventType} from 'enums/public_event_type';
import actions from 'store/actions';
import {useDispatch, useSelector} from 'react-redux';
import IStoreState from 'store/types';
import {SeminarViewEnum, ModalHandlerProps} from '.';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import Controls from '../../controls';

interface IProps {
  calendarEvent: IPublicEvent;
  calendarStartTime: number;
  isEventProcessing: boolean;
  onModalHandler?: (props: ModalHandlerProps) => void;
}

export const Item: FC<IProps> = ({calendarEvent, calendarStartTime, isEventProcessing, onModalHandler}: IProps) => {
  const dispatch = useDispatch();
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const memberId = useSelector((state: IStoreState) => state.auth.memberId);
  const [isProcessing, setIsProcessing] = useState(false);

  const {event, date, title, isPast, isBookAvailable, renderColumn, intersectionColumns} = calendarEvent;
  const {id, type, startTime, duration, memberPrice, currency, description} = event;
  const isSeminar = type === PublicEventType.SEMINAR;

  const classes = [
    duration === settings.calendarTimeStep ? 'minimal' : '',
    isPast ? 'past' : '',
    renderColumn ? 'column-indent column-indent-' + renderColumn : '',
    intersectionColumns ? 'columns columns-' + intersectionColumns : '',
  ]
    .filter(Boolean)
    .join(' ');

  const onBookClick = async (view?: SeminarViewEnum) => {
    setShowDetails(false);
    if (onModalHandler) {
      onModalHandler({
        type: event.type,
        eventId: id,
        eventTitle: title,
        date: calendarEvent.date,
        seminarView: view,
        hasMemberPrice: !!event.memberPrice,
      });
    } else {
      setIsProcessing(true);
      dispatch(actions.status.status());

      try {
        const updatedEvent = await actions.publicEvents.book(
          {eventId: id, eventTitle: title, memberId, date, action: 'book', eventType: type, hasPrice: memberPrice > 0},
          dispatch
        );
        dispatch(actions.events.replaceListItem(updatedEvent));
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const onCancel = async () => {
    const {leadAttendanceId, date} = calendarEvent;
    const action = leadAttendanceId ? 'cancel-with-buddy' : ('cancel' as const);
    setIsProcessing(true);

    try {
      const updatedEvent = await actions.publicEvents.book(
        {
          memberId,
          eventId: id,
          eventTitle: title,
          date,
          action,
          leadAttendanceId,
          eventType: calendarEvent.event.type,
          hasPrice: memberPrice > 0,
        },
        dispatch
      );
      dispatch(actions.events.replaceListItem(updatedEvent));
    } finally {
      setIsProcessing(false);
    }
  };

  const showIsProcessing = isProcessing || isEventProcessing;

  return (
    <div className={`cell event ${classes}`} style={utils.getCssProperties(calendarStartTime, calendarEvent)}>
      {calendarEvent.isBookedByMe && (!calendarEvent.event.memberPrice || calendarEvent.isPaid) && (
        <div className="booked-text">Booked</div>
      )}
      {calendarEvent.isBookedByMe && !!calendarEvent.event.memberPrice && !calendarEvent.isPaid && (
        <div className="booked-text text-danger">Pending</div>
      )}
      <div>{title}</div>
      <div>{utils.numberToDayTime(startTime)}</div>
      {!isPast && (
        <>
          {isSeminar && !memberId && (
            <>
              <div>{getPriceLabel(calendarEvent, 'member')}</div>
              <div>{getPriceLabel(calendarEvent, 'lead')}</div>
            </>
          )}
          {calendarEvent.isBookedByMe && (
            <div className="position-relative">
              <button className="btn btn-danger mx-1 mt-1" onClick={onCancel} disabled={showIsProcessing}>
                Cancel
              </button>
              {showIsProcessing && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  pulse
                  className="book-spinner"
                  style={{color: calendarEvent.props.style.color}}
                />
              )}
            </div>
          )}
          {!calendarEvent.isBookedByMe && (
            <div className="position-relative">
              {!isBookAvailable && <>FULL</>}
              {isBookAvailable && (
                <>
                  <button
                    className="btn btn-warning"
                    disabled={showIsProcessing}
                    onClick={() => onBookClick(isSeminar ? SeminarViewEnum.MEMBER : undefined)}>
                    Book
                  </button>
                  {isSeminar && !memberId && (
                    <button
                      className="btn btn-warning ml-2"
                      disabled={showIsProcessing}
                      onClick={() => onBookClick(SeminarViewEnum.NON_MEBMER)}>
                      Non-Member
                    </button>
                  )}
                  {isSeminar && memberId && (memberPrice > 0 || description) && (
                    <button
                      className="btn btn-info ml-2"
                      disabled={showIsProcessing}
                      onClick={() => setShowDetails(true)}>
                      Details
                    </button>
                  )}
                  {showIsProcessing && (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      pulse
                      className="book-spinner"
                      style={{color: calendarEvent.props.style.color}}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
      {showDetails && (
        <Controls.Modal
          title={`${title} ${moment(date).format('Do MMM')} ${utils.numberToDayTime(startTime)}`}
          size="large"
          onClose={() => setShowDetails(false)}>
          <div className="text-dark text-left">
            {memberPrice > 0 && (
              <div className="row">
                <label className="col-sm-3 col-form-label">Member Price:</label>
                <div className="col-sm-9 col-form-label">
                  {memberPrice} {currency}
                </div>
              </div>
            )}
            {description && (
              <div className="row">
                <label className="col-sm-3 col-form-label">Description:</label>
                <div
                  className="col-sm-9 col-form-label"
                  dangerouslySetInnerHTML={{__html: description.replace(/\n/g, '<br />')}}></div>
              </div>
            )}
            <button
              type="button"
              className="btn btn-secondary float-right mt-3 px-3 py-2"
              onClick={() => setShowDetails(false)}>
              Cancel
            </button>
            {!calendarEvent.isBookedByMe && isBookAvailable && (
              <button
                type="button"
                className="btn btn-warning float-right mr-2 mt-3 px-3 py-2"
                disabled={showIsProcessing}
                onClick={() => onBookClick(SeminarViewEnum.MEMBER)}>
                Book
              </button>
            )}
          </div>
        </Controls.Modal>
      )}
    </div>
  );
};

const getPriceLabel = ({event}: IPublicEvent, type: 'member' | 'lead'): string => {
  const label = `${type === 'member' ? 'Member' : 'Non-Member'} Price: `;

  if (type === 'member' && event.memberPrice > 0) {
    return label + `${event.memberPrice} ${event.currency}`;
  }

  if (type === 'lead' && event.leadPrice > 0) {
    return label + `${event.leadPrice} ${event.currency}`;
  }

  return label + 'Free';
};
